import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Fund } from "@/types/Fund";

export interface FormValues {
  name: string;
  fundKey?: string;
  fundCalendarId: number;
}

// Props interface - defines the input parameters for the hook
interface UseFundFormModalControllerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: FormValues) => Promise<void>;
  fund?: Fund;
}

// Return type interface - defines what the hook returns to consumers
interface UseFundFormModalController {
  form: ReturnType<typeof useForm<FormValues>>;
  handleSubmit: (values: FormValues) => Promise<void>;
  validateForm: (values: FormValues) => boolean;
}

export const useFundFormModalController = ({
  open,
  onOpenChange,
  onSubmit,
  fund,
}: UseFundFormModalControllerProps): UseFundFormModalController => {
  const form = useForm<FormValues>({
    defaultValues: {
      name: "",
      fundKey: "",
      fundCalendarId: undefined as unknown as number,
    },
  });

  // Reset form when modal opens or fund changes
  useEffect(() => {
    if (open) {
      form.reset({
        name: fund?.name || "",
        fundKey: fund?.fundKey || "",
        fundCalendarId: fund?.fundCalendarId as number,
      });
    }
  }, [open, fund, form]);

  // Custom validation
  const validateForm = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};

    if (!values.name || values.name.trim() === "") {
      errors.name = "Fund name is required";
      form.setError("name", { type: "manual", message: errors.name });
      return false;
    }

    if (!values.fundCalendarId) {
      errors.fundCalendarId = "Fund calendar is required";
      form.setError("fundCalendarId", {
        type: "manual",
        message: errors.fundCalendarId,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (values: FormValues) => {
    if (validateForm(values)) {
      await onSubmit(values);
      onOpenChange(false);
    }
  };

  return {
    form,
    handleSubmit,
    validateForm,
  };
};
